import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import axios from "axios";
import { forwardRef, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input";
import { authData } from "../../components/GetAuth";
import { usePlacesWidget } from "react-google-autocomplete";
import styles from "./list.module.css";

const CustomInput = forwardRef((props, ref) => (<TextField {...props} inputRef={ref} id="outlined-basic" label="Phone*" variant="outlined" />));

const AddDriverForm = ({ setFormOpen, setAllData, setSnackbarOpen, setSnackbarMessage, setSnackbarType, setLoading, editData }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const { register, handleSubmit, control, setValue, setError, watch, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      type: "",
      driver_type: "",
      wage_type: "",
      latitude: "",
      longitude: ""
    }
  });

  const [driverType, setDriverType] = useState("pickup");
  const [selectPCountry, setSelectPCountry] = useState("GB");
  const [selectDCountry, setSelectDCountry] = useState("GH");

  useEffect(() => {
    if (auth?.company && auth?.company?.pickup_country) {
      setSelectPCountry(auth?.company?.pickup_country);
    }
    if (auth?.company && auth?.company?.delivery_country) {
      setSelectDCountry(auth?.company?.delivery_country);
    }
  }, [auth?.company]);

  useEffect(() => {
    if (editData) {
      setValue("type", editData?.type);
      setValue("name", editData?.name);
      setValue("email", editData?.email);
      setValue("phone", editData?.phone);
      //setValue("house_no", editData?.house_no);
      setValue("address1", editData?.address);
      setValue("address2", editData?.address);
      setValue("driver_type", editData?.driver_type);
      setValue("wage_type", editData?.wage_type);
      setValue("latitude", editData?.latitude);
      setValue("longitude", editData?.longitude);

      if (parseInt(editData?.type) === 3) {
        setDriverType("pickup");
      }

      if (parseInt(editData?.type) === 4) {
        setDriverType("delivery");
      }
    }
  }, [editData, setValue, auth]);

  const onSubmit = (data) => {
    if (driverType === "pickup") {
      data = { ...data, address: data?.address1, location: selectPCountry };
    } else {
      data = { ...data, address: data?.address2, location: selectDCountry };
    }

    delete data?.address1;
    delete data?.address2;

    setLoading(true);

    if (editData) {
      axios.post(process.env.REACT_APP_API_URL + "v1/drivers/" + editData?.id, data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setFormOpen(false);
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setAllData((prev) => {
            let temp = prev.map((i) => {
              if (i.id === editData?.id) {
                return response.data.data;
              }
              return i;
            });
            return temp;
          });
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
        if (response.data.status === "VALIDATIONERROR") {
          const serverErrors = response.data.errors;

          // Loop backend validation errors
          Object.keys(serverErrors).forEach((field) => {
            setError(field, {
              type: "server",
              message: serverErrors[field][0], // Laravel style
            });
          });;
        }
      }).catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
    } else {
      axios.post(process.env.REACT_APP_API_URL + "v1/drivers", data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setFormOpen(false);
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setAllData((prev) => {
            return [response.data.data].concat(prev);
          });
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
        if (response.data.status === "VALIDATIONERROR") {
          const serverErrors = response.data.errors;

          // Loop backend validation errors
          Object.keys(serverErrors).forEach((field) => {
            setError(field, {
              type: "server",
              message: serverErrors[field][0], // Laravel style
            });
          });;
        }
      }).catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
    }
  };

  let { ref: ref1 } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("address1", place.formatted_address);
      setValue('latitude', place.geometry.location.lat());
      setValue('longitude', place.geometry.location.lng());
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: selectPCountry },
    },
  });

  const { ref: ref2 } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("address2", place.formatted_address);
      setValue('latitude', place.geometry.location.lat());
      setValue('longitude', place.geometry.location.lng());
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: selectDCountry },
    },
  });

  return (<form onSubmit={handleSubmit(onSubmit)}>
    <div className={`${styles.FormMainRow}`}>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Type*</InputLabel>
          <Controller
            control={control}
            {...register("type", { required: "This field is Required" })}
            render={({ field }) => (
              <Select
                label="Type*"
                {...field}
                disabled={editData}
                onChange={(e) => {
                  field.onChange(e);

                  if (e.target.value === 3) {
                    setDriverType("pickup");
                  }

                  if (e.target.value === 4) {
                    setDriverType("delivery");
                  }
                }}
              >
                <MenuItem value={3}>Pickup Driver</MenuItem>
                <MenuItem value={4}>Delivery Driver</MenuItem>
              </Select>
            )}
          />
        </FormControl>
        {errors.type && (<span className={`ErrorM`}>{errors?.type?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupFull} FormGroup`}>
        <Controller
          control={control}
          {...register("name", { required: "This field is Required" })}
          render={({ field }) => (<TextField {...field} label="Full Name*" variant="outlined" />)}
        />
        {errors.name && (<span className={`ErrorM`}>{errors?.name?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupHalf} FormGroup`}>
        <Controller
          control={control}
          {...register("email", {
            required: "This field is Required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
          render={({ field }) => (<TextField {...field} label="Email*" variant="outlined" disabled={editData} />
          )} />
        {errors.email && (<span className={`ErrorM`}>{errors?.email?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupHalf} FormGroup`}>
        <Controller
          control={control}
          {...register("phone", {
            required: "This field is Required",
            validate: isValidPhoneNumber,
          })}
          render={({ field }) => (<PhoneInput {...field} defaultCountry={driverType === "pickup" ? selectPCountry : selectDCountry} inputComponent={CustomInput} />)}
        />
        {errors.phone && errors.phone.type !== "validate" && (<span className={`ErrorM`}>{errors.phone.message}</span>)}
        {errors.phone && errors.phone.type === "validate" && (<span className={`ErrorM`}>Please enter valid phone</span>)}
      </div>
      {!editData && (<>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            {...register("password", {
              required: "This field is Required",
            })}
            control={control}
            render={({ field }) => (<TextField {...field} label="Password*" variant="outlined" type="password" />)}
          />
          {errors.password && (<span className={`ErrorM`}>{errors?.password?.message}</span>)}
        </div>
        <div className={`${styles.FormGroupHalf} FormGroup`}>
          <Controller
            {...register("conf_password", {
              required: "This field is Required",
              validate: (val) => {
                if (watch("password") !== val) {
                  return "Your passwords do no match";
                }
              },
            })}
            control={control}
            render={({ field }) => (<TextField {...field} id="outlined-basic" label="Confirm Password*" variant="outlined" type="password" />)}
          />
          {errors.conf_password && (<span className={`ErrorM`}>{errors?.conf_password?.message}</span>)}
        </div>
      </>
      )}
      {/*<div className={`${styles.FormGroupHalf} FormGroup`}>
        <Controller
          control={control}
          {...register("house_no", { required: "This field is Required" })}
          render={({ field }) => (<TextField {...field} label="House No*" variant="outlined" />)}
        />
        {errors.house_no && (<span className={`ErrorM`}>{errors?.house_no?.message}</span>)}
      </div>*/}
      <div className={`${styles.FormGroupFull} FormGroup`} style={{ display: driverType === "pickup" ? "block" : "none" }}>
        <Controller
          control={control}
          {...register("address1", {
            required: driverType === "pickup" ? true : false,
          })}
          render={({ field }) => (<TextField {...field} label="Address*" variant="outlined" inputRef={ref1} placeholder="" />)}
        />
        {errors.address1 && (<span className={`ErrorM`}>This field is Required</span>)}
      </div>

      <div className={`${styles.FormGroupFull} FormGroup`} style={{ display: driverType === "delivery" ? "block" : "none" }}>
        <Controller
          control={control}
          {...register("address2", {
            required: driverType === "delivery" ? true : false,
          })}
          render={({ field }) => (<TextField {...field} label="Address*" variant="outlined" inputRef={ref2} placeholder="" />)}
        />
        {errors.address2 && (<span className={`ErrorM`}>This field is Required</span>)}
      </div>
      <div className={`${styles.FormGroupHalf} FormGroup`}>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Driver Type*</InputLabel>
          <Controller
            control={control}
            {...register("driver_type", {
              required: "This field is Required",
            })}
            render={({ field }) => (<Select label="Driver Type*" {...field}>
              <MenuItem value="Direct Payroll">Direct Payroll</MenuItem>
              <MenuItem value="3rd Party Payroll">3rd Party Payroll</MenuItem>
            </Select>)}
          />
        </FormControl>
        {errors.driver_type && (<span className={`ErrorM`}>{errors?.driver_type?.message}</span>)}
      </div>
      <div className={`${styles.FormGroupHalf} FormGroup`}>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Wage Type*</InputLabel>
          <Controller
            control={control}
            {...register("wage_type", { required: "This field is Required" })}
            render={({ field }) => (<Select label="Wage Type*" {...field}>
              <MenuItem value="Hourly Basis">Hourly Basis</MenuItem>
              <MenuItem value="Monthly Basis">Monthly Basis</MenuItem>
            </Select>)}
          />
        </FormControl>
        {errors.wage_type && (<span className={`ErrorM`}>{errors?.wage_type?.message}</span>)}
      </div>
    </div>
    <div className="ButtonAction">
      <Button className="CancelPopupBU" onClick={(e) => setFormOpen(false)}>Cancel</Button>
      <Button className="SubmitPopupBU" type="submit">{editData ? "Update" : "Add"}</Button>
    </div>
  </form>);
};

export default AddDriverForm;